import React, { useState, useEffect, useContext } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAppContext } from './AppContext';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { db } from './firebase'; // Import Firestore instance
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  doc
} from 'firebase/firestore';
import AppContext from './AppContext';

const CategoryManagement = () => {
  const { shopId, t } = useAppContext();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editCategory, setEditCategory] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesRef = collection(db, 'categories');
        const q = query(categoriesRef, where('shopId', '==', shopId), orderBy('name', 'asc')); // Sort by 'name'
        const categorySnapshot = await getDocs(q);
        const categoryList = categorySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCategories(categoryList);
      } catch (err) {
        setError(t('Failed to load categories. Please try again.'));
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [shopId]);

  const handleEditClick = (category) => {
    setEditCategory({ ...category });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditCategory(null);
  };

  const handleSaveCategory = async () => {
    try {
      if (editCategory?.id) {
        // Update existing category
        const categoryDoc = doc(db, 'categories', editCategory.id);
        await updateDoc(categoryDoc, { name: editCategory.name });
      } else {
        // Add new category
        await addDoc(collection(db, 'categories'), {
          name: editCategory.name,
          shopId, // Include shopId
        });
      }

      handleCloseDialog();
      const categoriesRef = collection(db, 'categories');
      const q = query(categoriesRef, where('shopId', '==', shopId), orderBy('name', 'asc'));
      const categorySnapshot = await getDocs(q);
      const categoryList = categorySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoryList);
    } catch (err) {
      setError(t('Failed to save the category. Please try again.'));
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      const categoryDoc = doc(db, 'categories', id);
      await deleteDoc(categoryDoc);
      const updatedCategories = categories.filter((c) => c.id !== id);
      setCategories(updatedCategories);
    } catch (err) {
      setError(t('Failed to delete the category. Please try again.'));
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEditCategory({ ...editCategory, [name]: value });
  };

  return (
    <Container>
      <Typography variant="h4" mb={3} gutterBottom>
        {t('Category Management')}
      </Typography>

      {loading && <CircularProgress />} {/* Show loading spinner */}
      {error && <Alert severity="error">{error}</Alert>} {/* Show error alert if any */}

      {!loading && !error && (
        <>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenDialog(true)}
            sx={{ mb: 2 }}
            disableElevation
          >
            {t('Add Category')}
          </Button>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('Category Name')}</TableCell>
                  <TableCell>{t('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>{category.name}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditClick(category)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteCategory(category.id)}
                        color="secondary"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editCategory ? t('Edit Category') : t('Add Category')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label={t('Category Name')}
            fullWidth
            value={editCategory?.name || ''}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('Cancel')}</Button>
          <Button onClick={handleSaveCategory}>{t('Save')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CategoryManagement;
